import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { constSelector, useRecoilValue } from 'recoil';
import { Box, Button, DialogActions, DialogContent, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { ModalActionProps } from 'models/AutoModals';
import { VoyageStatus } from 'models/VoyageModel';
import { CompanyType } from 'models/CompanyModel';
import DropSheet from 'components/dropZone/DropSheet';
import companyList from 'recoil/atoms/companyList';
import voyageFamily from 'recoil/atoms/voyageFamily';
import auth from 'recoil/atoms/auth';
import loadingYardList from 'recoil/selectors/loadingYardList';
import Api from 'utils/api';


type UploadCarsSpreadsheetDialogProps = {
    requiredColumns: string[],
    arbitraryColumns: string[],
    secondaryColumns: string[],
    consigneeColumns: string[],
    notifyColumns: string[],
    dealerColumns: string[],
} & ModalActionProps<{ file: File, cols: {[k: string]: number} }>

const UploadCarsSpreadsheetDialog = ({
                                         requiredColumns,
                                         arbitraryColumns,
                                         secondaryColumns,
                                         consigneeColumns,
                                         notifyColumns,
                                         dealerColumns,
                                         onFinish,
                                     }: UploadCarsSpreadsheetDialogProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation('cars');
    const [ yardId, setYardId ] = useState('');
    const [ voyageId, setVoyageId ] = useState('');
    const [ shipperId, setShipperId ] = useState('');
    const [ file, setFile ] = useState<File>();
    const [ titles, setTitles ] = useState<{pos: number, title: string}[]>();
    const yards = useRecoilValue(loadingYardList);
    const voyages = useRecoilValue(voyageFamily(VoyageStatus.Pending));
    const companies = useRecoilValue(my.type === CompanyType.Master ? companyList(CompanyType.Shipper) : constSelector([]));
    const curYard = yards.find(y => y.id === Number(yardId));

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        onFinish({
            cols: Object.fromEntries([...new FormData(event.currentTarget).entries()].map(e => [e[0], e[1] === '' ? -1 : Number(e[1])])),
            file: file!,
            ...(yardId ? {yardId} : {}),
            ...(voyageId ? {voyageId} : {}),
            ...(shipperId ? {shipperId} : {}),
        });
    };

    const getNames = (file: File) => {
        setFile(file);
        Api.util.sheetColNames(file).then(res => setTitles(res.data.map((t, i) => ({pos: i, title: t})).filter(t => t.title)))
    };

    const guessColumn = (col: string) => {
        const split = col.replaceAll('_', ' ');

        let title = titles?.find(t => t.title.trim().toLowerCase().replaceAll('_', ' ') === split);

        if (!title) {
            switch (col) {
                case 'chassis_number':
                    title = titles?.find(t => t.title.toLowerCase().startsWith('chassi'));
                    break;
                case 'make':
                    title = titles?.find(t => t.title.toLowerCase().startsWith('make'));
                    break;
                case 'model':
                    title = titles?.find(t => t.title.toLowerCase() === 'car name');
                    break;
                case 'unit_price':
                    title = titles?.find(t => t.title.toLowerCase() === 'sell price');
                    break;
                case 'price_terms':
                    title = titles?.find(t => t.title.toLowerCase() === 'fob');
                    break;
                case 'engine_size':
                    title = titles?.find(t => t.title.toLowerCase() === 'cc');
                    break;
                case 'consignee_phone':
                    title = titles?.find(t => t.title.toLowerCase().startsWith('consignee tel'));
                    break;
                case 'consignee_last_name':
                    title = titles?.find(t => t.title.toLowerCase().startsWith('consignee name') || t.title.toLowerCase() === 'consignee');
                    break;
                case 'consignee_first_name':
                    title = titles?.find(t => t.title.toLowerCase().startsWith('consignee name') || t.title.toLowerCase() === 'consignee');
                    break;
                case 'dealer_last_name':
                case 'dealer_first_name':
                    title = titles?.find(t => t.title.toLowerCase() === 'customer name');
                    break;
                case 'notify_last_name':
                case 'notify_first_name':
                    title = titles?.find(t => t.title.toLowerCase() === 'broker');
                    break;
                case 'length':
                    title = titles?.find(t => t.title.toLowerCase() === 'l');
                    break;
                case 'width':
                    title = titles?.find(t => t.title.toLowerCase() === 'w');
                    break;
                case 'height':
                    title = titles?.find(t => t.title.toLowerCase() === 'h');
                    break;
                case 'export_cert_reference_num':
                    title = titles?.find(t => t.title.toLowerCase().includes('reference num'));
                    break;
                case 'export_cert_valid_until':
                    title = titles?.find(t => t.title.toLowerCase().includes('valid'));
                    break;
            }
        }

        return title ? title.pos : '';
    };

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                {!file && (
                    <>
                        <strong>Step 1: Upload file</strong>
                        <DropSheet upload={getNames} title="xls, xlsx, csv"/>

                        <Typography color="text.disabled">Tip: You can prepare spreadsheet by changing the column names with those used here</Typography>
                    </>
                )}

                {file && !titles &&
                    <Box>Preparing...</Box>
                }

                {titles && (
                    <>
                        <strong>Step 2: Define voyage (if not set in table)</strong>
                        <Grid container columnSpacing={2} mb={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    id="yardId"
                                    label={t('yard')}
                                    fullWidth
                                    select
                                    value={yardId}
                                    onChange={e => {
                                        setYardId(e.target.value);
                                        setVoyageId('');
                                    }}
                                >
                                    <MenuItem value=""><em>{t('unknown')}</em></MenuItem>
                                    {yards.map(c => <MenuItem key={c.id} value={c.id}>{c.port.name} {c.name}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    id="voyageId"
                                    label={t('voyage')}
                                    fullWidth
                                    select
                                    value={voyageId}
                                    onChange={e => setVoyageId(e.target.value)}
                                >
                                    {!curYard && <MenuItem value=""><em>{t('select_yard')}</em></MenuItem>}
                                    {curYard && <MenuItem value=""><em>{t('unknown')}</em></MenuItem>}
                                    {curYard && voyages.filter(v => v.pol.id === curYard.portId)
                                        .map(v => <MenuItem key={v.id} value={v.id}>{v.number}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                {companies.length > 0 &&
                                    <TextField
                                        id="shipper"
                                        label={t('shipper')}
                                        fullWidth
                                        select
                                        value={shipperId}
                                        onChange={e => setShipperId(e.target.value)}
                                    >
                                        <MenuItem value=""><em>{t('use_from_table')}</em></MenuItem>
                                        {companies.map(c => <MenuItem key={c.id} value={c.id}>{c.cleanName}</MenuItem>)}
                                    </TextField>
                                }
                            </Grid>
                        </Grid>
                        <strong>Step 3: Define  column names</strong>
                        <Grid container columnSpacing={2}>
                            <Grid item xs={12} sm>
                                {requiredColumns.map(col => (
                                    <TextField
                                        key={col}
                                        id={col}
                                        name={col}
                                        label={col.replaceAll('_', ' ')}
                                        fullWidth
                                        required
                                        select
                                        defaultValue={guessColumn(col)}
                                    >
                                        {titles.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                                    </TextField>
                                ))}
                                {arbitraryColumns.map(col => (
                                    <TextField
                                        key={col}
                                        id={col}
                                        name={col}
                                        label={col.replaceAll('_', ' ')}
                                        fullWidth
                                        select
                                        defaultValue={guessColumn(col)}
                                    >
                                        {titles.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                                    </TextField>
                                ))}
                            </Grid>
                            <Grid item xs={12} sm>
                                {secondaryColumns.map(col => (
                                    <TextField
                                        key={col}
                                        id={col}
                                        name={col}
                                        label={col.replaceAll('_', ' ')}
                                        fullWidth
                                        select
                                        defaultValue={guessColumn(col)}
                                    >
                                        {titles.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                                    </TextField>
                                ))}
                            </Grid>
                            <Grid item xs={12} sm>
                                {consigneeColumns.map(col => (
                                    <TextField
                                        key={col}
                                        id={col}
                                        name={col}
                                        label={col.replaceAll('_', ' ')}
                                        fullWidth
                                        select
                                        defaultValue={guessColumn(col)}
                                    >
                                        {titles.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                                    </TextField>
                                ))}
                            </Grid>
                            <Grid item xs={12} sm>
                                {notifyColumns.map(col => (
                                    <TextField
                                        key={col}
                                        id={col}
                                        name={col}
                                        label={col.replaceAll('_', ' ')}
                                        fullWidth
                                        select
                                        defaultValue={guessColumn(col)}
                                    >
                                        {titles.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                                    </TextField>
                                ))}
                            </Grid>
                            <Grid item xs={12} sm>
                                {dealerColumns.map(col => (
                                    <TextField
                                        key={col}
                                        id={col}
                                        name={col}
                                        label={col.replaceAll('_', ' ')}
                                        fullWidth
                                        select
                                        defaultValue={guessColumn(col)}
                                    >
                                        {titles.map(t => <MenuItem key={t.pos} value={t.pos}>{t.title}</MenuItem>)}
                                    </TextField>
                                ))}
                            </Grid>
                        </Grid>
                    </>
                )}
            </DialogContent>

            {titles && (
                <DialogActions>
                    <Button type="submit">{t('upload')}</Button>
                </DialogActions>
            )}
        </Box>
    );
};



export default UploadCarsSpreadsheetDialog;
